<template>
  <div v-if="!isLoading" class="gap-2 flex items-center md:items-end">
    <div v-if="stickyFooterText || stickyFooterQuoteText" class="text-white text-xl3-bold">
      <Text :field="{ value: premiumDetails ? stickyFooterText : stickyFooterQuoteText }" />
    </div>
    <div v-if="stickySmallText || stickySmallQuoteText" class="text-white text-base-bold mb-1">
      <Text :field="{ value: premiumDetails ? stickySmallText : stickySmallQuoteText }" />
    </div>
  </div>
  <DotLottieVue v-else autoplay style="width: 40px; height: 40px" loop :data="loadingIconData" />
  <FormKit
    type="submit"
    :label="buttonText"
    :disabled="formStore?.disableAllSteps || isLoading || disableSubmit"
    inputClass="btn btn-primary btn-md w-[126px]"
    outerClass="ml-auto"
    @click="buttonClicked"
  />
</template>
<script lang="ts" setup>
import { formatCurrency } from '@/utils/text-transforms';
import { computed } from 'vue';
import loadingData from '@/assets/animated-icons/Loading_dots_white.json';
import { QuotePremiumDetails } from '@/interfaces/entities/premium-details.interface';
import { Field } from '@sitecore-jss/sitecore-jss-vue';
import { DotLottieVue } from '@lottiefiles/dotlottie-vue';
import { Text } from '@sitecore-jss/sitecore-jss-vue';
import { useFormStore } from '@/store';
import { InsuranceQuoteSummary } from '@/interfaces';
import { PaymentFrequency } from '@/enums/payment-frequency';

// Props
// ------------------------------------------------------------- //
const props = defineProps<{
  fields: Record<string, Field<any>>;
  policyInRenewal: boolean;
  buttonText: string;
  isLoading?: boolean;
  premiumDetails?: QuotePremiumDetails;
  insuranceQuoteSummary?: InsuranceQuoteSummary;
  paymentFrequency?: PaymentFrequency;
  disableSubmit?: boolean;
}>();

const loadingIconData = loadingData as any;

const formStore = useFormStore();

// Self service premium details text
// ------------------------------------------------------------- //

const stickyFooterText = computed(() => {
  let stickyFootText = '';
  const quotePremiumDetails = props.premiumDetails;
  if (quotePremiumDetails?.premiumChanged) {
    if (quotePremiumDetails?.annualRefund) {
      stickyFootText = formatCurrency(quotePremiumDetails?.annualRefund, 2);
    } else {
      stickyFootText = props.premiumDetails?.monthlyNonResponsiveDeposit
        ? formatCurrency(props.premiumDetails?.monthlyNonResponsiveDeposit ?? 0, 2)
        : props.premiumDetails?.annualNonResponsivePremium
          ? props.policyInRenewal
            ? formatCurrency(props.premiumDetails?.annualNonResponsivePremium ?? 0, 2)
            : formatCurrency(props.premiumDetails?.annualNonResponsiveAmountDue ?? 0, 2)
          : formatCurrency(props.premiumDetails?.annualResponsiveAmountDue ?? 0, 2);
    }
  }
  return stickyFootText;
});

const stickySmallText = computed(() => {
  let stickyFootText = '';
  const quotePremiumDetails = props.premiumDetails;
  if (quotePremiumDetails?.premiumChanged) {
    if (quotePremiumDetails?.annualRefund) {
      stickyFootText = props.fields.refundText.value;
    } else {
      stickyFootText = quotePremiumDetails?.monthlyNonResponsiveDeposit
        ? props.policyInRenewal
          ? props.fields.renewalFirstMonthlyPaymentLabel.value
          : props.fields.nextMonthlyPaymentsLabel?.value
        : props.policyInRenewal
          ? props.fields.yearLabelNoSymbol.value
          : props.fields.annualLabel.value;
    }
  } else {
    stickyFootText = props.fields.noChangesToPremium.value;
  }
  return stickyFootText;
});

// Quote texts
// ------------------------------------------------------------- //

const stickyFooterQuoteText = computed(() => {
  let stickyFootText = '';
  const insuranceQuoteSummary = props.insuranceQuoteSummary;
  if (insuranceQuoteSummary) {
    stickyFootText =
      props.paymentFrequency === PaymentFrequency.Monthly
        ? formatCurrency(props.insuranceQuoteSummary?.monthlyDeposit ?? 0, 2)
        : formatCurrency(props.insuranceQuoteSummary?.yearlyAnnualPremium ?? 0, 2);
  }
  return stickyFootText;
});
const stickySmallQuoteText = computed(() => {
  let stickyFootText = '';
  const insuranceQuoteSummary = props.insuranceQuoteSummary;
  if (insuranceQuoteSummary) {
    stickyFootText =
      props.paymentFrequency === PaymentFrequency.Monthly
        ? props.fields.monthlyLabel.value
        : props.fields.yearLabel.value;
  }
  return stickyFootText;
});

const emit = defineEmits(['buttonClicked']);

// Functions
// ------------------------------------------------------------- //

const buttonClicked = (values: any) => {
  emit('buttonClicked', values);
};
</script>
